import type { Event } from '@bugsnag/js'
import { doitEtreNotifiee } from '~/utils/bugsnag-filter'

/**
 * Écarte de Bugsnag les erreurs du fonctionnement normal — 401, 403, 404.
 *
 * Le module `nuxt-bugsnag` ne propose pas de filtre, et son `nuxt.config.ts` ne
 * peut pas en porter un : le module étale `options.config` dans
 * `runtimeConfig.public.bugsnag`, que ses plugins relisent au démarrage, or le
 * `runtimeConfig` est sérialisé et une fonction n'y survit pas. Poser `onError`
 * dans la configuration du module serait donc sans effet.
 *
 * Le filtre passe par `addOnError()`, l'API du SDK prévue pour enregistrer un
 * callback après le démarrage. Un callback qui renvoie `false` empêche l'envoi
 * de l'événement — il n'est donc pas décompté du quota, là où une règle posée
 * dans l'interface de Bugsnag le consommerait quand même.
 *
 * Ce plugin s'exécute après celui du module : `nuxt-bugsnag` enregistre le sien
 * par `addPlugin()` lors du `setup()`, donc avant les plugins applicatifs de
 * `app/plugins/`. Il n'est volontairement pas suffixé `.client` : le plugin du
 * module ne l'est pas non plus.
 *
 * `useBugsnag()` est un auto-import du module, et il n'existe pas toujours :
 * quand le module ne l'a pas déclaré, la référence n'est liée à rien et
 * l'évaluer lève `useBugsnag is not defined`. D'où le `typeof` posé avant
 * l'appel, et non sur son résultat — un test placé après arriverait trop tard.
 * La cause exacte de cette disparition n'est pas tranchée ici : la garde
 * protège quelle qu'elle soit, et ne coûte rien quand l'auto-import est présent.
 *
 * Le second test reste nécessaire et couvre un autre cas : faute de clé,
 * `Bugsnag.start()` échoue et le module installe de lui-même un client factice,
 * dont `addOnError` est une fonction vide — l'appel reste alors sans effet,
 * sans erreur.
 *
 * Le détail des codes retenus et leur justification sont dans
 * `app/utils/bugsnag-filter.ts`. Le pendant Nitro est dans
 * `server/plugins/bugsnag-filtre.ts`.
 */
export default defineNuxtPlugin(() => {
  const bugsnag = typeof useBugsnag === 'function' ? useBugsnag() : null

  if (typeof bugsnag?.addOnError !== 'function') return

  bugsnag.addOnError((event: Event) => {
    // `false` annule l'envoi ; toute autre valeur le laisse passer.
    return doitEtreNotifiee(event.originalError)
  })
})
